import { routePattern, routes } from '@/utils/routes';
import UpgradeDialog from '@rhg/components/upgrade-dialog';
import { useAccessCode } from '@rhg/hooks/use-access-code';
import { useBuyUpEligibility } from '@rhg/hooks/use-buy-up-eligibility';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useRegistrationDialog } from '@rhg/hooks/use-registration-dialog';
import {
    UpgradeDialogOpenQueryKey,
    useUpgradeDialog,
} from '@rhg/hooks/use-upgrade-dialog';
import { useUpgradeHandoffClaim } from '@rhg/hooks/use-upgrade-handoff-claim';
import { logger } from '@rhg/libs/logger';
import {
    accessCodeQueryString,
    sponsorCodeQueryString,
} from '@rhg/providers/magic-link';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

const excludedPages = [
    routes.auth,
    routes.login,
    routes.standaloneScreening,
    routes.standaloneResults,
    routes.signup,
    routes.saml,
    routes.sso,
    routes.ssoInfo,
    routes.registration,
    routes.reconsent,
    routes.partnerMemberActivation,
    routePattern.partnerMemberActivationToken,
    // Error / expired / not-found / non-US-resident pages.
    routes.error({}),
    routes.expired,
    routes.notFound,
    routes.nonUsResident,
    // FTUE / onboarding-related pages.
    routes.onboarding({}),
    routes.topics({ isFtue: false }),
    routes.goals({ isFtue: false }),
    routes.conditions({ isFtue: false }),
    routes.results({ isFtue: false }),
    routes.concierge({}),
    // `routes.guide` requires a `guideId` and always bakes the query string
    // into its return value, so this uses the bare-path constant instead.
    routes.guideBase,
];

export function TransitionDialogManager() {
    const { pathname } = useRouter();
    const {
        willShowDialog,
        deadline,
        isPastDeadline,
        destinationPartner,
        dismissDialog,
    } = useBuyUpEligibility();
    const { closeUpgradeDialog } = useUpgradeDialog();
    const { isRegistrationDialogOpen, showRegistrationDialog } =
        useRegistrationDialog();
    const { setAccessCode } = useAccessCode();
    const { claimAccessCode } = useUpgradeHandoffClaim();

    const onDismiss = useCallback(() => {
        dismissDialog();
        closeUpgradeDialog().catch((err) =>
            logger.error(
                'transitionPartner.closeUpgradeDialog.failed',
                {},
                err as Error
            )
        );
    }, [closeUpgradeDialog, dismissDialog]);

    const onUpgrade = useCallback(async () => {
        if (!destinationPartner) {
            return;
        }

        try {
            // EVE-14052 dedicates `slug` (not `CirclePartner.uniqueCode`) to
            // this: seeding the registration entry as an access_code. A
            // stale sponsor_code from an earlier, unfinished magic-link
            // visit must not survive into this flow — it would route the
            // auto-submit magic-link step, which Buy-Up must never show.
            //
            // MagicLinkProvider's accessCode resolves state before query —
            // seeding the query alone leaves a stale setAccessCode() value
            // from an earlier attempt this session in charge, so it has to
            // be set here too (state only — showRegistrationDialog already
            // owns the query side).
            //
            // Claiming first, synchronously, closes a race with
            // RegistrationDialogManager: showRegistrationDialog()'s own
            // query write (registrationFlow, rdo) is async, so setAccessCode
            // alone would let RegistrationDialogManager see this same access
            // code resolve to an eligible upgrade before registrationFlow
            // catches up, and reclaim the dialog this call is opening.
            claimAccessCode(destinationPartner.slug);
            setAccessCode(destinationPartner.slug);
            const didOpen = await showRegistrationDialog({
                currentStep: RegistrationFlowStep.UpgradeIntake,
                registrationFlow: RegistrationFlow.UpgradeSponsorship,
                forcedQuery: {
                    [accessCodeQueryString]: destinationPartner.slug,
                },
                // `udo` is dropped as part of this same atomic query write:
                // calling closeUpgradeDialog() afterwards would replace from
                // its stale pre-navigation query closure and wipe out the
                // params just written here.
                dropQueryKeys: [
                    sponsorCodeQueryString,
                    UpgradeDialogOpenQueryKey,
                ],
            });
            // router.replace resolves false (not a throw) if the
            // navigation didn't go through — dismissing anyway would
            // reproduce the "neither dialog visible" bug this refactor
            // was meant to eliminate.
            if (didOpen !== false) {
                dismissDialog();
            }
        } catch (err) {
            logger.error(
                'transitionPartner.showRegistrationDialog.failed',
                {},
                err as Error
            );
        }
    }, [
        claimAccessCode,
        destinationPartner,
        dismissDialog,
        setAccessCode,
        showRegistrationDialog,
    ]);

    const isExcludedPage = excludedPages.includes(pathname);

    if (!willShowDialog || isExcludedPage || isRegistrationDialogOpen) {
        return null;
    }

    return (
        <UpgradeDialog
            isOpen
            deadlineDate={deadline ?? undefined}
            isPastDeadline={isPastDeadline}
            onUpgrade={() => void onUpgrade()}
            onDismiss={onDismiss}
        />
    );
}
